// Historik: one timeline per object instead of one row per day. The daily ledger says, per day and finding, which objects are new
// and which are gone; this puts the days of one object (in one finding) together: found, gone, found again.
//
// An event without object names (the ledger only keeps names for up to 30 objects, and not for every finding) stays a row of its
// own for the whole finding, so nothing is lost.

const stateOf = (kind) => (kind === 'new' ? 'open' : kind === 'res' ? 'res' : 'unc');
const stamp = (e) => `${e.d}${e.t || ''}`;
export const dayDiff = (a, b) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000);

export function buildTimelines(events, { today, from, mergeGapDays = 7 }) {
  const sorted = [...(events || [])].sort((a, b) => stamp(a).localeCompare(stamp(b)));
  const map = new Map();
  sorted.forEach((e, i) => {
    const names = Array.isArray(e.o) && e.o.length > 0 ? e.o.filter(Boolean) : null;
    if (!names) {
      const key = `#${i}`;
      map.set(key, { key, group: true, name: e.title, g: e.g, title: e.title, area: e.area, areaKey: e.area, tone: e.tone, n: e.n, events: [{ k: e.k, d: e.d, t: e.t, first: e.first || null }] });
      return;
    }
    names.forEach((name) => {
      const key = `${e.g}::${name}`;
      let t = map.get(key);
      if (!t) { t = { key, group: false, name, g: e.g, title: e.title, area: e.area, areaKey: e.area, tone: e.tone, events: [] }; map.set(key, t); }
      t.tone = e.tone || t.tone;
      // The first-seen day on a "gone" event is the earliest of its objects: only trusted for an event about one object.
      t.events.push({ k: e.k, d: e.d, t: e.t, first: names.length === 1 ? (e.first || null) : null });
    });
  });

  return [...map.values()].map((t0) => {
    // Gone and back within a week (a finding built on a window of events, or a reading that flickers) is the same stretch: both
    // steps are left out, so the object stays open through the gap. If nothing is left, it was open before the period and still is.
    const kept = [];
    t0.events.forEach((e) => {
      const prev = kept[kept.length - 1];
      if (e.k === 'new' && prev && prev.k !== 'new' && dayDiff(prev.d, e.d) <= mergeGapDays) { kept.pop(); return; }
      kept.push(e);
    });
    const carried = kept.length === 0;
    const t = { ...t0, events: carried ? [{ k: 'new', d: t0.events[t0.events.length - 1].d, t: t0.events[t0.events.length - 1].t, first: null, carried: true }] : kept };
    const last = t.events[t.events.length - 1];
    const first = t.events[0];
    const state = stateOf(last.k);
    // How many times it has shown up: each "new" is one, and one that was already there when the period began counts once more.
    const times = carried ? 1 : t.events.filter((e) => e.k === 'new').length + (first.k !== 'new' ? 1 : 0);
    // Stretches where it was open, for the small bar: from found (or from the start of the period) to gone (or to today).
    const spans = [];
    let openFrom = carried ? from : first.k !== 'new' ? (first.first && first.first >= from ? first.first : from) : null;
    t.events.forEach((e) => {
      if (e.carried) return;
      if (e.k === 'new') { if (openFrom === null) openFrom = e.d; return; }
      if (openFrom !== null) { spans.push({ from: openFrom, to: e.d, open: false }); openFrom = null; }
      else if (e === first) spans.push({ from: e.first && e.first >= from ? e.first : from, to: e.d, open: false });
    });
    if (openFrom !== null) spans.push({ from: openFrom, to: today, open: true });
    const lastNew = [...t.events].reverse().find((e) => e.k === 'new' && !e.carried);
    const openedKnown = state === 'open' ? !!lastNew : (first.k === 'new' || !!first.first);
    const openedOn = state === 'open' ? (lastNew ? lastNew.d : null) : (first.k === 'new' ? first.d : first.first || null);
    return { ...t, carried, state, times, spans, lastDay: last.d, lastAt: last.t, openedOn, openedKnown, days: openedOn ? dayDiff(openedOn, state === 'open' ? today : last.d) : null };
  }).sort((a, b) => `${b.lastDay}${b.lastAt || ''}`.localeCompare(`${a.lastDay}${a.lastAt || ''}`));
}
